import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";
const SCRIPT_ID = "google-recaptcha-script";
const CALLBACK_NAME = "__bobbinRecaptchaLoaded";

function waitForRecaptchaRender(timeoutMs = 8000) {
  if (typeof window === "undefined") return Promise.resolve(false);
  if (typeof window.grecaptcha?.render === "function") return Promise.resolve(true);

  return new Promise((resolve) => {
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      if (typeof window.grecaptcha?.render === "function") {
        window.clearInterval(timer);
        resolve(true);
        return;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        window.clearInterval(timer);
        resolve(false);
      }
    }, 100);
  });
}

function loadRecaptchaScript() {
  if (!SITE_KEY || typeof document === "undefined") return Promise.resolve(false);
  if (typeof window.grecaptcha?.render === "function") return Promise.resolve(true);

  return new Promise((resolve) => {
    window[CALLBACK_NAME] = () => {
      waitForRecaptchaRender().then(resolve);
    };

    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      waitForRecaptchaRender().then(resolve);
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/api.js?onload=${CALLBACK_NAME}&render=explicit`;
    script.async = true;
    script.defer = true;
    script.onload = () => waitForRecaptchaRender().then(resolve);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

const RecaptchaField = forwardRef(function RecaptchaField({ onVerify }, ref) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [enabled] = useState(Boolean(SITE_KEY));

  useImperativeHandle(ref, () => ({
    reset() {
      if (typeof window.grecaptcha?.reset === "function" && widgetIdRef.current !== null) {
        window.grecaptcha.reset(widgetIdRef.current);
      }
      onVerify("");
    },
  }), [onVerify]);

  useEffect(() => {
    let cancelled = false;

    loadRecaptchaScript().then((loaded) => {
      if (cancelled || !loaded || !containerRef.current || widgetIdRef.current !== null) return;
      if (typeof window.grecaptcha?.render !== "function") return;

      widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
        sitekey: SITE_KEY,
        callback: (token) => onVerify(token),
        "expired-callback": () => onVerify(""),
        "error-callback": () => onVerify(""),
      });
    });

    return () => {
      cancelled = true;
    };
  }, [onVerify]);

  if (!enabled) return null;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3">
      <div ref={containerRef} />
    </div>
  );
});

export default RecaptchaField;
